import { memo, useEffect, useState } from 'react'
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi'
import { BsThreeDots } from 'react-icons/bs'
import { TbRefresh } from 'react-icons/tb'
import { LuSearch } from 'react-icons/lu'
import { Box, Button, Table as CTable, Flex, IconButton, Input, InputGroup, InputLeftElement, Popover, PopoverContent, Select, Spinner, TableContainer, Tbody, Td, Text, Th, Thead, Tr, Tooltip } from '@chakra-ui/react'
import { type ColumnDef, type FilterFn, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { rankItem } from '@tanstack/match-sorter-utils'

import { NoDataFound } from '../../../shared/components'

export interface IPagination {
  currentPage: number;
  perPage: number;
  searchTerm: string;
  total: number | undefined | undefined;
  totalPages: number;
  refreshTable: boolean;
}

interface IProps {
  // eslint-disable-next-line
  columns: ColumnDef<any>[]
  rowData: any
  pagination: IPagination
  setPagination: any
  title?: string
  showSearchBar?: boolean
  showTools?: boolean
  loading?: boolean
  filterJSX?: any
  height?: string | undefined
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value)

  // Store the itemRank info
  addMeta({
    itemRank,
  })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

function Table(props: IProps) {
  const {
    columns,
    title,
    rowData,
    pagination,
    setPagination,
    showSearchBar,
    showTools,
    loading,
    height,
  } = props
  const [data, setData] = useState(() => [...(rowData ?? [])])
  const [goToPage, setGoToPage] = useState<number>(pagination?.currentPage ?? 1)
  const [mounted, setMounted] = useState(false)
  const [search, setSearch] = useState<string>('')
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter,
    },
  })

  useEffect(() => {
    if (pagination.totalPages <= goToPage) setPagination({ ...pagination, currentPage: goToPage })
  }, [goToPage])

  const toggle = (index: number) => {
    table.getAllLeafColumns()?.[index]?.toggleVisibility()
    table.getAllLeafColumns()?.[index].getToggleVisibilityHandler()
  }

  useEffect(() => {
    columns?.forEach((item, index) => {
      if (item?.enableHiding) {
        toggle(index)
      }
    })
  }, [table])

  useEffect(() => {
    setData(rowData)
  }, [rowData])

  useEffect(() => {
    setGoToPage(pagination?.currentPage)
  }, [pagination?.currentPage])

  const searchFn = (searchItem: string) => {
    setSearch(searchItem)
  }

  let timer: any
  useEffect(() => {
    if (mounted) {
      timer = setTimeout(() => {
        setPagination({ ...pagination, searchTerm: search })
      }, 800)

      return () => {
        clearTimeout(timer)
      }
    } else {
      setMounted(true)
    }
  }, [search])

  const nextFn = () => {
    if (pagination?.currentPage === pagination?.totalPages) return
    let tempcurrentPage = pagination.currentPage
    tempcurrentPage = tempcurrentPage + 1
    setPagination({ ...pagination, currentPage: tempcurrentPage })
  }

  const prevFn = () => {
    if (pagination?.currentPage === 1) return
    let tempcurrentPage = pagination.currentPage
    tempcurrentPage = tempcurrentPage - 1
    setPagination({ ...pagination, currentPage: tempcurrentPage })
  }

  const handlePage = (page: number) => {
    if (pagination?.currentPage === page) return
    setPagination({ ...pagination, currentPage: page })
  }

  const handlePerPage = (perpage: number) => {
    setPagination({ ...pagination, perpage })
  }

  const refreshData = () => {
    // update refresh to pagination
    setPagination((prev: IPagination) => ({ ...prev, refreshTable: !prev.refreshTable }))
  }

  return (
    <Box className='table'>
      <Box pt='4' pb='1' my='2' borderColor='gray.200'>
        <Flex
          direction={{
            lg: 'row',
            base: 'column',
          }}
          gap='5'
          alignItems='center'
          justify='space-between'
          px='2px'
        >
          {showSearchBar ? (
            <Tooltip label="Search">
              <Box display={'flex'} alignItems='center'>
                  <InputGroup size='sm'>
                    <Input
                      pr='4.5rem'
                      size={'sm'}
                      maxWidth='300'
                      onChange={(e) => {
                        searchFn(String(e.target.value))
                      }}
                      value={search}
                      placeholder="Search"
                    />
                    <InputLeftElement cursor={'pointer'} width='2rem'>
                      <LuSearch />
                    </InputLeftElement>
                  </InputGroup>
                
              </Box>
            </Tooltip>
          ) : (
            <Box></Box>
          )}

          {showTools && (
            <Flex
              gap='3'
              justifyContent={'flex-end'}
              flexWrap={'wrap'}
              sx={{
                svg: {
                  marginBottom: '2px',
                },
              }}
            >
              <Popover trigger={'hover'} placement={'bottom-start'} size={'sm'}>
               
                <PopoverContent>
                  <Flex flexDirection='column' gap='2' p='2' maxH={'50vh'} overflowY={'scroll'}>
                    <div>
                      <label>
                        <input
                          {...{
                            type: 'checkbox',
                            checked: table.getIsAllColumnsVisible(),
                            onChange: table.getToggleAllColumnsVisibilityHandler(),
                          }}
                        />
                        &nbsp;{"TABLE_LABELS?.all"}
                      </label>
                    </div>
                    {table.getAllLeafColumns().map((item, index) => {
                      if (item?.depth > 0) return null
                      return (
                        <div key={item.id} className='px-1'>
                          <label>
                            <input
                              {...{
                                type: 'checkbox',
                                checked: item.getIsVisible(),
                                onChange: item.getToggleVisibilityHandler(),
                              }}
                            />
                            &nbsp;
                            {
                              // @ts-ignore
                              columns?.[index].header()?.props.children
                            }
                          </label>
                        </div>
                      )
                    })}
                  </Flex>
                </PopoverContent>
              </Popover>

              {props?.filterJSX && props?.filterJSX}
              <Tooltip label="Refresh">
                <Button size='sm' onClick={refreshData} variant='primary'>
                  <TbRefresh /> &nbsp; <Text pt='1'>Refresh</Text>
                </Button>
              </Tooltip>

            </Flex>
          )}
        </Flex>
      </Box>

      <TableContainer
        id='custom--table'
        border='1px solid #bdbdbd'
        // ref={printRef}
        overflowY='auto'
        height={height ?? 'calc(100vh - 275px)'}
      >
        <CTable
          className='table custom--table'
          sx={{
            thead: {
              tr: {
                th: {
                  textTransform: 'capitalize',
                  paddingTop: '10px',
                  paddingBottom: '8px',
                  fontWeight: '500',
                  color: 'white',
                  border: '1px solid #ebebeb',
                  '&:first-of-child': {
                    borderRight: 'none',
                  },
                  '&:last-of-child': {
                    borderLeft: 'none',
                  },
                },
              },
            },
            tbody: {
              tr: {
                td: {
                  border: '1px solid #ebebeb',
                  '&:first-of-child': {
                    borderRight: 'none',
                  },
                  '&:last-of-child': {
                    borderLeft: 'none',
                  },
                  paddingY: '8px',
                  span: {
                    fontSize: '14px',
                  },
                  button: {
                    fontSize: '10px',
                    height: '25px',
                    width: '25px',
                    padding: '0px',
                  },
                  _hover: {
                    backgroundColor: 'gray.100',
                  },
                },
                _hover: {
                  backgroundColor: '#f5f5f5',
                },
              },
            },
          }}
        >
          <Thead
            border='1px solid #1c4c70'
            background='#1c4c70'
            maxHeight='30px'
            position='sticky'
            top='0px'
            zIndex='2'
          >
            {table.getHeaderGroups().map((headerGroup, index: number) => (
              <Tr key={index}>
                {headerGroup.headers.map((header, index: number) => {
                  return (
                    <Th
                      key={index}
                      colSpan={header?.colSpan}
                      style={{
                        width: header.column.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  )
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={100}>
                  <Flex
                    sx={{
                      height: '70vh',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Spinner thickness='3px' speed='0.3s' emptyColor='gray.200' color='#005050' size='xl' />
                    <Text pt='3' fontSize={'14px'} color='gray'>
                      {"loading"}
                    </Text>
                  </Flex>
                </Td>
              </Tr>
            ) : rowData?.length > 0 ? (
              table?.getRowModel()?.rows?.map((row, index) => (
                <Tr key={index}>
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <Td key={index}>
                        <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                      </Td>
                    )
                  })}
                </Tr>
              ))
            ) : (
              <Tr height={'66vh'}>
                <Td colSpan={100}>
                  <Flex
                    sx={{
                      height: '100%%',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <NoDataFound />
                    <Tooltip label="Refresh">
                      <Box
                        onClick={refreshData}
                        background='#005050'
                        color='white'
                        paddingY='5px'
                        paddingX='20px'
                        fontSize='13px'
                        borderRadius='2px'
                        cursor='pointer'
                        display='flex'
                        alignItems='center'
                        gap='2'
                        sx={{
                          svg: {
                            marginBottom: '2px',
                          },
                        }}
                      >
                        <TbRefresh /> Refresh
                      </Box>
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            )}
          </Tbody>
        </CTable>
      </TableContainer>

      <Box border='1px solid #bdbdbd' borderTop={'none'}>
        <Flex
          gap='5'
          px='4'
          py='3'
          borderBottom='1px'
          borderColor='gray.200'
          background='gray.100'
          justifyContent={'space-between'}
          alignItems='center'
        >
          <Text fontSize={'13px'}>{title}</Text>

          {pagination?.total && pagination?.total > 0 ? (
            <Flex justifyContent={'flex-end'} flexWrap='wrap' alignItems={'center'} gap='10'>
              <Tooltip label="Rows per page">
                <Flex
                  fontSize='15px'
                  alignItems='center'
                  mr={'2'}
                  sx={{
                    fontSize: '13px',
                  }}
                >
                  Rows per page
                  <Select
                    ml={'2'}
                    value={pagination.perPage}
                    onChange={(e) => {
                      handlePerPage(Number(e.target.value))
                    }}
                    size='xs'
                    width='60px'
                    fontSize='15px'
                    sx={{
                      outline: 'none',
                    }}
                    _focus={{
                      boxShadow: 'none',
                    }}
                    _hover={{
                      cursor: 'pointer',
                    }}
                  >
                    {[1, 5, 10, 20, 30, 40, 50].map((pageSize, index) => (
                      <option key={index} value={pageSize} style={{ fontSize: '15px' }}>
                        {pageSize?.toString()}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </Tooltip>

              <Tooltip label="Total Data">
                <Box>
                  {pagination?.total ? (
                    <Text
                      sx={{
                        fontSize: '13px',
                      }}
                    >
                      Total Data : {pagination?.total?.toString()}
                    </Text>
                  ) : null}
                </Box>
              </Tooltip>
              <Flex gap='2' alignItems={'center'}>
                <Text fontSize={'13px'}>Go to page : </Text>
                <Tooltip label="Go to page">
                  <form action=''>
                    <Input
                      sx={{
                        height: '22px',
                        width: '40px',
                        borderRadius: '1px',
                        textAlign: 'center',
                        fontSize: '13px',
                        outline: '1px solid #c2c2c2',
                        _focus: {
                          boxShadow: 'none',
                        },
                      }}
                      type='number'
                      min={1}
                      max={pagination?.totalPages ?? 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value)
                        if (value < 1 || value > pagination?.totalPages) return
                        setGoToPage(parseInt(e.target.value))
                      }}
                      value={goToPage}
                    />
                    <input
                      onClick={(e) => {
                        e.preventDefault()
                        pagination?.totalPages && goToPage <= pagination?.totalPages && handlePage(goToPage)
                      }}
                      type='submit'
                      style={{
                        display: 'none',
                      }}
                    />
                  </form>
                </Tooltip>
              </Flex>
              <Flex
                gap='2'
                sx={{
                  button: {
                    fontSize: '13px',
                    height: '25px',
                    width: '25px',
                    _disabled: {
                      cursor: 'not-allowed',
                    },
                  },
                }}
              >
                <Tooltip label="Previous">
                  <IconButton
                    sx={{
                      padding: '0px',
                    }}
                    isDisabled={pagination?.currentPage === 1}
                    variant='outline'
                    colorScheme='#255d88'
                    aria-label='previous'
                    onClick={prevFn}
                    size='sm'
                    icon={<HiOutlineArrowNarrowLeft color='#255d88' />}
                  />
                </Tooltip>
                {pagination?.totalPages &&
                  [...Array(pagination?.totalPages)].map((_, index) => {
                    const pageNumber = index + 1
                    if (
                      pageNumber === 1 ||
                      pageNumber === pagination?.totalPages ||
                      (pageNumber >= pagination?.currentPage - 1 && pageNumber <= pagination?.currentPage + 1)
                    ) {
                      return (
                        <Button
                          sx={{
                            padding: '0px',
                          }}
                          key={pageNumber}
                          size='sm'
                          variant={pageNumber === pagination?.currentPage ? 'primary' : 'outline'}
                          colorScheme='#255d88'
                          onClick={() => {
                            handlePage(pageNumber)
                          }}
                        >
                          {pageNumber?.toString()}
                        </Button>
                      )
                    } else if (
                      (pageNumber === pagination?.currentPage - 2 && pageNumber > 1) ||
                      (pageNumber === pagination?.currentPage + 2 && pageNumber < pagination?.totalPages)
                    ) {
                      return (
                        <IconButton
                          sx={{
                            padding: '0px',
                          }}
                          key={pageNumber}
                          variant='outline'
                          colorScheme='#255d88'
                          aria-label='previous'
                          size='sm'
                          icon={<BsThreeDots color='#255d88' />}
                        />
                      )
                    }
                    return null
                  })}
                <Tooltip placement={'left'} label={"go to next page"}>
                  <IconButton
                    sx={{
                      padding: '0px',
                    }}
                    isDisabled={pagination?.currentPage === pagination?.totalPages}
                    variant='outline'
                    colorScheme='#255d88'
                    aria-label='next'
                    onClick={nextFn}
                    size='sm'
                    icon={<HiOutlineArrowNarrowRight color='#255d88' />}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      </Box>
    </Box>
  )
}

Table.defaultProps = {
  showSearchBar: true,
  showTools: true,
  loading: true,
  nepaliSearch: true,
}

export default memo(Table)